
import * as THREE from 'three';
import { FloorElement } from './types';

export class SlabGeometrySolver {

    /**
     * Create the geometry for a Floor (Flat or Shaped).
     */
    static createFloorMesh(floor: FloorElement): THREE.Mesh {
        const shapeEditor = floor.geometryData.shapeEditor;
        // A shape editor with no modifications (no picked points, no split
        // lines) describes a plain flat slab — BIM renders it exactly like
        // an unedited floor. Only route to the shaped builder when there is
        // actual shape-edit data to consume.
        const hasShapeData = shapeEditor && shapeEditor.isEnabled &&
            (shapeEditor.points.length > 0 || shapeEditor.splitLines.length > 0);
        if (hasShapeData) {
            return this.createShapedSlab(floor);
        } else {
            return this.createFlatSlab(floor);
        }
    }

    private static createFlatSlab(floor: FloorElement): THREE.Mesh {
        const shape = new THREE.Shape();
        const boundary = floor.geometryData.boundary;

        if (boundary.length < 3) return new THREE.Mesh();

        shape.moveTo(boundary[0][0], boundary[0][1]);
        for (let i = 1; i < boundary.length; i++) {
            shape.lineTo(boundary[i][0], boundary[i][1]);
        }
        shape.closePath();

        const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: floor.geometryData.thickness,
            bevelEnabled: false
        });

        // Extrude creates volume in +Z direction usually? No, "depth" is Z.
        // We usually want Top Face at Elevation.
        // So we translate down by thickness.
        geometry.translate(0, 0, -floor.geometryData.thickness);

        // Add Base Offset
        geometry.translate(0, 0, floor.geometryData.heightOffsetFromLevel); // + Level Elevation (handled by placement)

        const mesh = new THREE.Mesh(geometry);
        return mesh;
    }

    private static createShapedSlab(floor: FloorElement): THREE.Mesh {
        const data = floor.geometryData.shapeEditor!;
        const boundary = floor.geometryData.boundary;
        const thickness = floor.geometryData.thickness;

        // 1. Collect all points (Boundary + Internal Points)
        const vertices2D: { x: number, y: number, z: number, id?: string, isBoundary?: boolean }[] = [];

        // Boundary Points
        boundary.forEach((p, _idx) => {
            // Find if this boundary point has an override in points list
            // Usually boundary points inherit Z=0 unless modified
            const override = data.points.find(sp => sp.isBoundary && Math.abs(sp.position[0] - p[0]) < 0.001 && Math.abs(sp.position[1] - p[1]) < 0.001);
            const z = override ? override.offset : 0;
            vertices2D.push({ x: p[0], y: p[1], z: z, id: override?.id, isBoundary: true });
        });

        // Internal Points
        data.points.forEach(p => {
            if (!p.isBoundary) {
                vertices2D.push({ x: p.position[0], y: p.position[1], z: p.offset, id: p.id, isBoundary: false });
            }
        });

        // 2. Triangulate
        // Since we don't have a Constrained Delaunay library, we'll implement a simple Bowyer-Watson or 
        // fall back to a naive fan if convex, or use a robust pattern if possible.
        // Given complexity limits, we will use a "Center Point Fan" fallback if internal points exist, 
        // OR try to map to a Delaunator-like structure if we had the library.

        // Let's implement a naive "Connect All to All and Filter" or standard 2D Delaunay 
        // (O(n^2) is fine for typically < 50 points in a slab editor)

        const indices = this.computeDelaunayIndices(vertices2D);

        // 3. Build Geometry
        const geometry = new THREE.BufferGeometry();
        const positions: number[] = [];

        // We need Top Face and Bottom Face
        // Top Face vertices use the computed Z offsets.
        // Bottom Face vertices uses computed Z offsets - thickness (Constant Thickness model)
        // OR Variable Thickness? BIM floors can be "Variable" layer.
        // We will assume Constant Thickness (Offsets apply to whole stack) for now unless Variable is set.
        // Let's assume Constant Thickness -> Bottom follows Top.

        // Top Vertices
        vertices2D.forEach(v => {
            positions.push(v.x, v.y, v.z);
        });

        // Bottom Vertices (duplicated for flat shading norms, or shared if smooth? Floors are sharp usually)
        // For a solid, we need distinct vertices for top/bottom/sides?
        // Let's just create Top Surface for now as the primary visual. 
        // To make it solid, we replicate.

        const N = vertices2D.length;
        vertices2D.forEach(v => {
            positions.push(v.x, v.y, v.z - thickness);
        });

        const indicesArray: number[] = [];

        // Top Face (CCW)
        for (let i = 0; i < indices.length; i += 3) {
            indicesArray.push(indices[i], indices[i + 1], indices[i + 2]);
        }

        // Bottom Face (CW) - Offset by N
        for (let i = 0; i < indices.length; i += 3) {
            indicesArray.push(indices[i] + N, indices[i + 2] + N, indices[i + 1] + N);
        }

        // Sides
        // Boundary edges need walls.
        // Identify boundary edges from triangulation? 
        // Or walk the boundary loop since we know it.
        for (let i = 0; i < boundary.length; i++) {
            const next = (i + 1) % boundary.length;
            const topCurrent = i;
            const topNext = next;
            const botCurrent = i + N;
            const botNext = next + N;

            // Quad: TC, BC, BN, TN
            indicesArray.push(topCurrent, botCurrent, botNext);
            indicesArray.push(topCurrent, botNext, topNext);
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setIndex(indicesArray);
        geometry.computeVertexNormals();

        // Base Offset
        geometry.translate(0, 0, floor.geometryData.heightOffsetFromLevel);

        return new THREE.Mesh(geometry);
    }

    // Simple O(N^4) or O(N^3) Delaunay for small N
    private static computeDelaunayIndices(verts: { x: number, y: number }[]): number[] {
        const indices: number[] = [];
        const n = verts.length;
        if (n < 3) return [];

        // Super Triangle (Large enough to contain all)
        // We skip super triangle implementation and just check all triplets?
        // Check every triplet (i, j, k). If circumcircle contains no other point, it's a triangle.

        for (let i = 0; i < n - 2; i++) {
            for (let j = i + 1; j < n - 1; j++) {
                for (let k = j + 1; k < n; k++) {
                    const ax = verts[i].x, ay = verts[i].y;
                    const bx = verts[j].x, by = verts[j].y;
                    const cx = verts[k].x, cy = verts[k].y;

                    // Check CCW
                    const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
                    if (cross <= 0) continue; // Skip Collinear or CW

                    // Circumcircle
                    const D = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
                    const Ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / D;
                    const Uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / D;
                    const R2 = (ax - Ux) ** 2 + (ay - Uy) ** 2;

                    let valid = true;
                    for (let m = 0; m < n; m++) {
                        if (m === i || m === j || m === k) continue;
                        const dist2 = (verts[m].x - Ux) ** 2 + (verts[m].y - Uy) ** 2;
                        if (dist2 < R2 - 0.0001) {
                            valid = false;
                            break;
                        }
                    }

                    if (valid) {
                        indices.push(i, j, k);
                    }
                }
            }
        }
        return indices;
    }

    /**
     * Compute volume of the slab: shoelace area of the boundary polygon
     * times the total slab thickness (m³).
     */
    static computeVolume(floor: FloorElement): number {
        const boundary = floor.geometryData.boundary;
        const thickness = floor.geometryData.thickness ?? 0;
        if (!boundary || boundary.length < 3 || thickness <= 0) return 0;

        let twiceArea = 0;
        for (let i = 0; i < boundary.length; i++) {
            const [x1, y1] = boundary[i];
            const [x2, y2] = boundary[(i + 1) % boundary.length];
            twiceArea += x1 * y2 - x2 * y1;
        }
        const area = Math.abs(twiceArea) / 2;
        return area * thickness;
    }
}
